// Iter 49 — Payroll & Shifts page.
// Month picker + per-employee summary table + simple shift calendar grid
// (one cell per day, badges = number of shifts that ended that day).
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, Users, Wallet, Plus, Trash2, Save, RefreshCw, Unlink, AlertTriangle } from "lucide-react";

function ymOf(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function daysInMonth(month) {
  const [y, m] = month.split("-").map(Number);
  return new Date(y, m, 0).getDate();
}

function firstWeekday(month) {
  // Monday-first: 0=Mon, 6=Sun
  const [y, m] = month.split("-").map(Number);
  const dow = new Date(y, m - 1, 1).getDay();
  return (dow + 6) % 7;
}

export default function Payroll() {
  const navigate = useNavigate();
  const location = useLocation();
  const [month, setMonth] = useState(() => ymOf(new Date()));
  const [summary, setSummary] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [staff, setStaff] = useState([]);
  const [busy, setBusy] = useState(false);
  // staff config form
  const [newStaffName, setNewStaffName] = useState("");
  const [newStaffRate, setNewStaffRate] = useState("");
  // advance form
  const [advName, setAdvName] = useState("");
  const [advAmount, setAdvAmount] = useState("");
  const [advNote, setAdvNote] = useState("Έναντι");
  const [advances, setAdvances] = useState([]);
  // Iter 50 — Google Calendar OAuth state
  const [gcal, setGcal] = useState({ configured: false, linked: false });
  const [gcalSyncing, setGcalSyncing] = useState(false);
  // Iter 51 — Private iCal Feed URL state
  const [icalCfg, setIcalCfg] = useState(null); // null while loading
  const [icalUrl, setIcalUrl] = useState("");
  const [syncEmail, setSyncEmail] = useState("");
  const [icalBusy, setIcalBusy] = useState(false);
  const [icalSyncBusy, setIcalSyncBusy] = useState(false);
  const loadGcalStatus = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/status");
      setGcal(data);
    } catch { /* tolerate */ }
  };
  const loadIcalStatus = async () => {
    try {
      const { data } = await api.get("/calendar/ical/config");
      setIcalCfg(data);
      setSyncEmail(data.sync_email || "");
    } catch { setIcalCfg({ configured: false }); }
  };
  const saveIcalConfig = async () => {
    if (!icalUrl.trim() || !icalUrl.toLowerCase().startsWith("http")) {
      toast.error("Paste a valid https:// .ics calendar URL");
      return;
    }
    setIcalBusy(true);
    try {
      await api.post("/calendar/ical/config", {
        ical_url: icalUrl.trim(),
        sync_email: syncEmail.trim(),
      });
      toast.success("iCal feed saved · click Sync to pull events");
      setIcalUrl("");
      loadIcalStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setIcalBusy(false); }
  };
  const clearIcalConfig = async () => {
    if (!window.confirm("Disconnect iCal feed and remove all synced shifts?")) return;
    try {
      await api.delete("/calendar/ical/config");
      toast.success("iCal feed disconnected");
      loadIcalStatus();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const syncIcal = async () => {
    setIcalSyncBusy(true);
    try {
      const { data } = await api.post("/calendar/ical/sync");
      toast.success(`iCal · parsed ${data.parsed_events}/${data.raw_events} events (${data.skipped} skipped)`);
      loadIcalStatus();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally { setIcalSyncBusy(false); }
  };
  // Handle ?google_oauth=ok/error returned from the callback redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const flag = params.get("google_oauth");
    if (flag === "ok") {
      toast.success(`Google Calendar linked · ${params.get("email") || ""}`);
      navigate("/payroll", { replace: true });
      loadGcalStatus();
    } else if (flag === "error") {
      toast.error(`Google link failed: ${params.get("detail") || "unknown"}`);
      navigate("/payroll", { replace: true });
    }
  }, [location.search]);
  useEffect(() => { loadGcalStatus(); loadIcalStatus(); }, []);
  const signInWithGoogle = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/init");
      window.location.assign(data.authorization_url);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && typeof det === "object" && det.code === "google_oauth_not_configured") {
        toast.error(det.message || "Google OAuth not configured", { duration: 8000 });
      } else {
        toast.error(typeof det === "string" ? det : "Sign-in init failed");
      }
    }
  };
  const disconnectGoogle = async () => {
    if (!window.confirm("Unlink Google Calendar from this tenant?")) return;
    try {
      await api.delete("/oauth/calendar/disconnect");
      toast.success("Google Calendar unlinked");
      loadGcalStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Disconnect failed");
    }
  };
  const syncGoogle = async () => {
    setGcalSyncing(true);
    try {
      const { data } = await api.post("/calendar/sync");
      toast.success(`Synced ${data.parsed_events}/${data.raw_events} events (${data.skipped} skipped)`);
      load();
    } catch (e) {
      const det = e.response?.data?.detail;
      toast.error(typeof det === "string" ? det : (det?.message || "Sync failed"));
    } finally {
      setGcalSyncing(false);
    }
  };

  const load = async () => {
    setBusy(true);
    try {
      const [{ data: s }, { data: c }, { data: st }, { data: ad }] = await Promise.all([
        api.get(`/payroll/summary?month=${month}`),
        api.get(`/payroll/calendar?month=${month}`),
        api.get(`/payroll/staff`),
        api.get(`/payroll/advances?month=${month}`),
      ]);
      setSummary(s);
      setCalendar(c);
      setStaff(st.staff || []);
      setAdvances(ad.advances || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); }, [month]); // eslint-disable-line

  const upsertRate = async () => {
    if (!newStaffName.trim() || !newStaffRate) return;
    try {
      await api.post("/payroll/staff", {
        employee_name: newStaffName.trim(),
        hourly_rate: Number(newStaffRate),
      });
      setNewStaffName(""); setNewStaffRate("");
      toast.success("Rate saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const recordAdvance = async () => {
    if (!advName.trim() || !advAmount) return;
    try {
      await api.post("/payroll/advances", {
        employee_name: advName.trim(),
        month,
        amount: Number(advAmount),
        note: advNote,
      });
      setAdvName(""); setAdvAmount("");
      toast.success("Advance recorded");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteAdvance = async (id) => {
    try {
      await api.delete(`/payroll/advances/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const calCells = useMemo(() => {
    const days = daysInMonth(month);
    const offset = firstWeekday(month);
    const cells = [];
    for (let i = 0; i < offset; i++) cells.push(null);
    for (let d = 1; d <= days; d++) {
      const iso = `${month}-${String(d).padStart(2, "0")}`;
      const entries = (calendar?.by_day || {})[iso] || [];
      cells.push({ day: d, iso, entries });
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, calendar]);

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            🕒 Payroll & Βάρδιες
          </h1>
          <p className="text-slate-500 mt-1">
            Auto-computed from shift timestamps · advances deducted from net salary
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-widest font-bold text-slate-500">Month</label>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="payroll-month-picker"
            className="border-2 border-slate-300 rounded-md px-3 py-2 font-mono"
          />
        </div>
      </header>

      {/* Iter 50 — Google Calendar sign-in card */}
      <section
        className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4 shadow-sm space-y-4"
        data-testid="payroll-google-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-white border border-blue-300 flex items-center justify-center text-2xl shadow-sm">
              🔵
            </div>
            <div>
              <div className="font-black text-blue-900 tracking-tight">
                Google Calendar Sync
              </div>
              <div className="text-xs text-blue-700">
                {gcal.linked
                  ? `OAuth linked · ${gcal.linked_email || ""}`
                  : gcal.configured
                  ? "OAuth ready · Sign in to pull scheduled shifts"
                  : "OAuth not configured · use the private iCal feed below (zero credentials)"}
              </div>
              <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                Event format: <span className="bg-white px-1 rounded">Maria: 09:00-17:00 - Vizantiou</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!gcal.linked ? (
              <button
                onClick={signInWithGoogle}
                disabled={!gcal.configured}
                data-testid="payroll-google-signin-btn"
                className="bg-white hover:bg-blue-50 disabled:opacity-50 border-2 border-blue-400 text-blue-900 font-bold px-4 py-2 rounded shadow-sm flex items-center gap-2 text-sm"
              >
                <span className="text-lg">🔵</span>
                Sign in with Google / Σύνδεση με Google
              </button>
            ) : (
              <>
                <button
                  onClick={syncGoogle}
                  disabled={gcalSyncing}
                  data-testid="payroll-google-sync-btn"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded shadow-sm flex items-center gap-2 text-sm disabled:opacity-50"
                >
                  <RefreshCw className={`h-4 w-4 ${gcalSyncing ? "animate-spin" : ""}`} />
                  {gcalSyncing ? "Syncing…" : "Sync Now"}
                </button>
                <button
                  onClick={disconnectGoogle}
                  data-testid="payroll-google-disconnect-btn"
                  className="border-2 border-red-300 text-red-700 hover:bg-red-50 font-bold px-3 py-2 rounded text-sm flex items-center gap-1"
                  title="Unlink Google Calendar"
                >
                  <Unlink className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Iter 51 — Private iCal Feed URL sub-section (works without OAuth credentials) */}
        <div
          className="border-t-2 border-dashed border-blue-300 pt-4 space-y-3"
          data-testid="payroll-ical-card"
        >
          <div className="flex items-center gap-2">
            <div className="text-lg">📥</div>
            <div className="font-bold text-blue-900 tracking-tight">
              Private iCal Feed URL · Χωρίς Σύνδεση
            </div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-300 px-2 py-0.5 rounded-full font-bold ml-auto">
              Zero credentials needed
            </div>
          </div>
          <p className="text-xs text-slate-600">
            Get it from <b>Google Calendar → ⚙️ Settings → Settings for my calendars → [your calendar] → Integrate calendar → &quot;Secret address in iCal format&quot;</b>. Paste it once — the backend pulls + parses on demand.
          </p>

          <div className="grid sm:grid-cols-[2fr_1fr] gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-blue-800">
                📥 iCal Feed URL (private secret address)
              </label>
              <input
                type="url"
                value={icalUrl}
                onChange={(e) => setIcalUrl(e.target.value)}
                placeholder={icalCfg?.configured ? icalCfg.ical_url_masked : "https://calendar.google.com/calendar/ical/.../basic.ics"}
                data-testid="payroll-ical-url-input"
                className="w-full mt-1 border-2 border-blue-300 focus:border-blue-500 rounded px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-blue-800">
                📧 Google Sync Email Entry / Εισαγωγή Email Συγχρονισμού
              </label>
              <input
                type="email"
                value={syncEmail}
                onChange={(e) => setSyncEmail(e.target.value)}
                placeholder="giannisleo51@gmail.com"
                data-testid="payroll-ical-email-input"
                className="w-full mt-1 border-2 border-blue-300 focus:border-blue-500 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[11px] text-slate-600">
              {icalCfg?.configured ? (
                <>
                  <span className="font-bold text-emerald-700">✅ Feed configured</span>
                  {icalCfg.sync_email && <> · <span className="font-mono">{icalCfg.sync_email}</span></>}
                  {icalCfg.last_synced_at && (
                    <> · last sync {new Date(icalCfg.last_synced_at).toLocaleString()}</>
                  )}
                  {icalCfg.last_parsed_count != null && (
                    <> · {icalCfg.last_parsed_count}/{icalCfg.last_raw_count} parsed</>
                  )}
                  {icalCfg.last_error && (
                    <> · <span className="text-red-700">⚠ {icalCfg.last_error}</span></>
                  )}
                </>
              ) : (
                <span className="text-slate-500">No feed configured yet</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={saveIcalConfig}
                disabled={icalBusy || !icalUrl.trim()}
                data-testid="payroll-ical-save-btn"
                className="bg-white hover:bg-blue-50 border-2 border-blue-400 text-blue-900 font-bold px-3 py-1.5 rounded text-xs flex items-center gap-1 disabled:opacity-50"
              >
                <Save className="h-3 w-3" /> Save URL
              </button>
              <button
                onClick={syncIcal}
                disabled={icalSyncBusy || !icalCfg?.configured}
                data-testid="payroll-ical-sync-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded text-xs flex items-center gap-1 disabled:opacity-50"
              >
                <RefreshCw className={`h-3 w-3 ${icalSyncBusy ? "animate-spin" : ""}`} />
                {icalSyncBusy ? "Pulling…" : "Sync iCal"}
              </button>
              {icalCfg?.configured && (
                <button
                  onClick={clearIcalConfig}
                  data-testid="payroll-ical-clear-btn"
                  className="border-2 border-red-300 text-red-700 hover:bg-red-50 font-bold px-2 py-1.5 rounded text-xs flex items-center gap-1"
                  title="Disconnect iCal feed"
                >
                  <Unlink className="h-3 w-3" />
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Totals bar */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="payroll-totals">
          {[
            ["hours",      "Σ Hours",       summary.totals.hours, "text-slate-900"],
            ["gross",      "Σ Gross",       `€${summary.totals.gross_salary.toFixed(2)}`, "text-blue-700"],
            ["advances",   "Σ Advances",    `€${summary.totals.advances.toFixed(2)}`, "text-amber-700"],
            ["net",        "Σ Net Salary",  `€${summary.totals.net_salary.toFixed(2)}`,
              summary.totals.net_salary < 0 ? "text-red-700" : "text-emerald-700"],
          ].map(([k, l, v, c]) => (
            <div key={k} className="bg-white border-2 border-slate-200 rounded-xl p-4 shadow-sm">
              <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{l}</p>
              <p className={`text-3xl font-black tabular-nums mt-1 ${c}`}>{v}</p>
            </div>
          ))}
        </div>
      )}

      {/* Calendar grid */}
      <section className="bg-white border-2 border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-slate-700" />
          <h2 className="font-bold text-slate-900 tracking-tight">Shift Calendar · {month}</h2>
        </div>
        <div className="p-4 grid grid-cols-7 gap-1 text-xs" data-testid="payroll-calendar">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d} className="text-center font-bold text-slate-500 py-2">{d}</div>
          ))}
          {calCells.map((cell, i) => (
            <div
              key={i}
              className={`min-h-[70px] rounded-md border ${
                cell ? "border-slate-200 bg-slate-50/40" : "border-transparent"
              } p-1.5`}
            >
              {cell && (
                <>
                  <div className="text-[10px] font-bold text-slate-700">{cell.day}</div>
                  <div className="mt-0.5 space-y-0.5">
                    {cell.entries.slice(0, 3).map((e, ei) => (
                      <div
                        key={ei}
                        className="text-[9px] bg-blue-100 text-blue-900 rounded px-1 py-0.5 truncate"
                        title={`${e.employee_name} · ${e.location} · ${e.hours}h`}
                      >
                        {e.employee_name} · {e.hours}h
                      </div>
                    ))}
                    {cell.entries.length > 3 && (
                      <div className="text-[9px] text-slate-500">+{cell.entries.length - 3} more</div>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Per-employee table */}
      <section className="bg-white border-2 border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <Users className="h-5 w-5 text-slate-700" />
          <h2 className="font-bold text-slate-900 tracking-tight">Per-Employee Net Salary</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payroll-table">
            <thead>
              <tr className="text-left border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-4 py-2">Employee</th>
                <th className="px-4 py-2 text-right">Shifts</th>
                <th className="px-4 py-2 text-right">Hours</th>
                <th className="px-4 py-2 text-right">Scheduled</th>
                <th className="px-4 py-2 text-right">Δ (hrs)</th>
                <th className="px-4 py-2 text-right">€/hr</th>
                <th className="px-4 py-2 text-right">Gross</th>
                <th className="px-4 py-2 text-right">Advances</th>
                <th className="px-4 py-2 text-right">Net Salary</th>
              </tr>
            </thead>
            <tbody>
              {summary?.rows?.map((r) => {
                const dh = r.discrepancy_hours || 0;
                return (
                <tr key={r.employee_name} className="border-b border-slate-100 last:border-0 hover:bg-blue-50/30">
                  <td className="px-4 py-2 font-bold text-slate-900">{r.employee_name}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.shifts}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{r.hours.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-blue-700">
                    {r.scheduled_hours.toFixed(2)}
                  </td>
                  <td className={`px-4 py-2 text-right tabular-nums font-bold ${
                    Math.abs(dh) < 0.01 ? "text-slate-400"
                    : dh > 0 ? "text-red-700"
                    : "text-amber-700"}`}
                    data-testid={`payroll-discrepancy-${r.employee_name}`}
                  >
                    {Math.abs(dh) < 0.01 ? "—" : <span className="inline-flex items-center gap-1">
                      {dh > 0 && <AlertTriangle className="h-3 w-3" />}
                      {dh > 0 ? "+" : ""}{dh.toFixed(2)}
                    </span>}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">€{r.hourly_rate.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-blue-700">€{r.gross_salary.toFixed(2)}</td>
                  <td className="px-4 py-2 text-right tabular-nums text-amber-700">€{r.advances.toFixed(2)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums font-black ${r.net_salary < 0 ? "text-red-700" : "text-emerald-700"}`}>
                    €{r.net_salary.toFixed(2)}
                  </td>
                </tr>
              );})}
              {(!summary || summary.rows.length === 0) && (
                <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">{busy ? "Loading…" : "No payroll data for this month."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* Configuration: hourly rates + advance ledger */}
      <div className="grid lg:grid-cols-2 gap-6">
        <section className="bg-white border-2 border-slate-200 rounded-xl shadow-sm p-5">
          <h3 className="font-bold text-slate-900 tracking-tight mb-3 flex items-center gap-2">
            <Wallet className="h-5 w-5 text-blue-600" /> Hourly Rates
          </h3>
          <div className="flex items-end gap-2 mb-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Employee</label>
              <input
                value={newStaffName}
                onChange={(e) => setNewStaffName(e.target.value)}
                placeholder="Maria"
                className="w-full mt-1 border-2 border-slate-300 rounded px-3 py-2 text-sm"
                data-testid="payroll-staff-name-input"
              />
            </div>
            <div className="w-32">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">€/hr</label>
              <input
                type="number" step="0.01" min="0"
                value={newStaffRate}
                onChange={(e) => setNewStaffRate(e.target.value)}
                placeholder="6.50"
                className="w-full mt-1 border-2 border-slate-300 rounded px-3 py-2 text-sm tabular-nums"
                data-testid="payroll-staff-rate-input"
              />
            </div>
            <button
              onClick={upsertRate}
              data-testid="payroll-staff-save-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2 rounded text-sm flex items-center gap-1"
            >
              <Save className="h-4 w-4" /> Save
            </button>
          </div>
          <ul className="divide-y divide-slate-100 text-sm">
            {staff.map((s) => (
              <li key={s.employee_name} className="py-1.5 flex justify-between">
                <span className="font-semibold text-slate-900">{s.employee_name}</span>
                <span className="tabular-nums text-slate-700">€{s.hourly_rate.toFixed(2)} / hr</span>
              </li>
            ))}
            {staff.length === 0 && <li className="py-3 text-slate-400 text-center">No staff configured.</li>}
          </ul>
        </section>

        <section className="bg-white border-2 border-slate-200 rounded-xl shadow-sm p-5">
          <h3 className="font-bold text-slate-900 tracking-tight mb-3 flex items-center gap-2">
            <Plus className="h-5 w-5 text-amber-600" /> Cash Advances · Έναντι
          </h3>
          <div className="flex items-end gap-2 mb-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Employee</label>
              <input
                value={advName}
                onChange={(e) => setAdvName(e.target.value)}
                placeholder="Maria"
                className="w-full mt-1 border-2 border-slate-300 rounded px-3 py-2 text-sm"
                data-testid="payroll-advance-name-input"
              />
            </div>
            <div className="w-28">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">€ amount</label>
              <input
                type="number" step="0.01" min="0.01"
                value={advAmount}
                onChange={(e) => setAdvAmount(e.target.value)}
                placeholder="40"
                className="w-full mt-1 border-2 border-slate-300 rounded px-3 py-2 text-sm tabular-nums"
                data-testid="payroll-advance-amount-input"
              />
            </div>
            <button
              onClick={recordAdvance}
              data-testid="payroll-advance-save-btn"
              className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-4 py-2 rounded text-sm flex items-center gap-1"
            >
              <Save className="h-4 w-4" /> Log
            </button>
          </div>
          <ul className="divide-y divide-slate-100 text-sm">
            {advances.map((a) => (
              <li key={a.id} className="py-1.5 flex items-center justify-between gap-2">
                <span className="font-semibold text-slate-900 truncate">{a.employee_name}</span>
                <span className="text-xs text-slate-500 flex-1 truncate">{a.note}</span>
                <span className="tabular-nums text-amber-700 font-bold">€{a.amount.toFixed(2)}</span>
                <button
                  onClick={() => deleteAdvance(a.id)}
                  className="p-1 hover:bg-red-100 rounded text-red-600"
                  title="Delete"
                  data-testid={`payroll-advance-delete-${a.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
            {advances.length === 0 && <li className="py-3 text-slate-400 text-center">No advances this month.</li>}
          </ul>
        </section>
      </div>
    </div>
  );
}
